/**
 * Thin wrapper around the Telegram WebApp SDK (loaded globally via
 * telegram-web-app.js in index.html). Only the surface this Mini App
 * actually needs — no generic SDK re-export.
 */
export interface TelegramHapticFeedback {
  impactOccurred?(style: "light" | "medium" | "heavy" | "rigid" | "soft"): void;
  notificationOccurred?(type: "error" | "success" | "warning"): void;
  selectionChanged?(): void;
}

export interface TelegramUser {
  id?: number;
  is_bot?: boolean;
  first_name?: string;
  last_name?: string;
  username?: string;
  language_code?: string;
  is_premium?: boolean;
  [key: string]: unknown;
}

export interface TelegramWebApp {
  initData: string;
  initDataUnsafe: {
    start_param?: string;
    user?: TelegramUser;
    [key: string]: unknown;
  };
  HapticFeedback?: TelegramHapticFeedback;
  ready(): void;
  expand(): void;
  close(): void;
  sendData?(data: string): void;
  disableVerticalSwipes?(): void;
  onEvent(event: string, handler: () => void): void;
  showAlert?(message: string): void;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

export function getWebApp(): TelegramWebApp | null {
  return window.Telegram?.WebApp ?? null;
}

/** Signals Telegram the app is ready and switches to full-height scanner layout. */
export function initTelegramApp(webApp: TelegramWebApp): void {
  webApp.ready();
  webApp.expand();
  webApp.disableVerticalSwipes?.();
}

/**
 * The opaque scan-session token from the /scan deep link
 * (`t.me/<bot>/app?startapp=<token>`, spec §7). This is untrusted routing
 * data only — the token is re-verified server-side as part of the signed
 * `initData` payload sent to fit-bot's authenticate endpoint, never trusted
 * on its own.
 */
export function getStartParam(webApp: TelegramWebApp | null): string | null {
  const fromSdk = webApp?.initDataUnsafe?.start_param;
  if (fromSdk) return fromSdk;
  if (typeof window === "undefined") return null;
  try {
    const search = new URLSearchParams(window.location.search);
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    return (
      search.get("tgWebAppStartParam") ||
      search.get("startapp") ||
      search.get("start_param") ||
      hash.get("tgWebAppStartParam") ||
      hash.get("startapp") ||
      hash.get("start_param")
    );
  } catch {
    return null;
  }
}

/**
 * The raw, signed initData string. Never read `initDataUnsafe` fields for
 * authorization decisions (spec §6) — only this raw string, verified
 * server-side, may establish who the user is.
 */
export function getInitData(webApp: TelegramWebApp): string {
  return webApp.initData;
}

/**
 * Triggers Telegram WebApp haptic impact feedback ('light' | 'medium' | 'heavy' | 'rigid' | 'soft').
 * Safely degrades without throwing if Telegram WebApp or HapticFeedback is not available.
 */
export function triggerHapticImpact(
  style: "light" | "medium" | "heavy" | "rigid" | "soft" = "medium",
  webApp?: TelegramWebApp | null,
): void {
  try {
    const app = webApp ?? getWebApp();
    app?.HapticFeedback?.impactOccurred?.(style);
  } catch {
    // Safe degradation in non-Telegram or unsupported environments
  }
}

/**
 * Triggers Telegram WebApp haptic selection feedback.
 * Safely degrades without throwing if Telegram WebApp or HapticFeedback is not available.
 */
export function triggerHapticSelection(webApp?: TelegramWebApp | null): void {
  try {
    const app = webApp ?? getWebApp();
    app?.HapticFeedback?.selectionChanged?.();
  } catch {
    // Safe degradation in non-Telegram or unsupported environments
  }
}

/**
 * Triggers Telegram WebApp haptic notification feedback (e.g. 'success', 'error', 'warning').
 * Safely degrades without throwing if Telegram WebApp or HapticFeedback is not available.
 */
export function triggerHapticSuccess(webApp?: TelegramWebApp | null): void {
  try {
    const app = webApp ?? getWebApp();
    if (app?.HapticFeedback?.notificationOccurred) {
      app.HapticFeedback.notificationOccurred("success");
    } else if (app?.HapticFeedback?.impactOccurred) {
      app.HapticFeedback.impactOccurred("medium");
    }
  } catch {
    // Safe degradation in non-Telegram or unsupported environments
  }
}

/**
 * Triggers Telegram WebApp haptic notification feedback for errors.
 * Safely degrades without throwing if Telegram WebApp or HapticFeedback is not available.
 */
export function triggerHapticError(webApp?: TelegramWebApp | null): void {
  try {
    const app = webApp ?? getWebApp();
    app?.HapticFeedback?.notificationOccurred?.("error");
  } catch {
    // Safe degradation in non-Telegram or unsupported environments
  }
}

/**
 * Triggers Telegram WebApp haptic notification feedback with specified type ('error', 'success', 'warning').
 * Safely degrades without throwing if Telegram WebApp or HapticFeedback is not available.
 */
export function triggerHapticNotification(
  type: "error" | "success" | "warning",
  webApp?: TelegramWebApp | null,
): void {
  try {
    const app = webApp ?? getWebApp();
    app?.HapticFeedback?.notificationOccurred?.(type);
  } catch {
    // Safe degradation in non-Telegram or unsupported environments
  }
}

/**
 * Sends a string or JSON-serializable payload back to the Telegram bot via WebApp.sendData.
 * Safely degrades without throwing if Telegram WebApp or sendData is not available.
 */
export function sendTelegramData(data: unknown, webApp?: TelegramWebApp | null): boolean {
  try {
    const app = webApp ?? getWebApp();
    if (app && typeof app.sendData === "function") {
      const payload = typeof data === "string" ? data : JSON.stringify(data);
      app.sendData(payload);
      return true;
    }
  } catch (err) {
    console.warn("Failed to send data via Telegram WebApp:", err);
  }
  return false;
}

/**
 * Safely extracts the user's language code from Telegram initDataUnsafe or signed initData string.
 */
export function getTelegramLanguageCode(webApp?: TelegramWebApp | null): string | null {
  try {
    const app = webApp ?? getWebApp();
    if (!app) return null;
    if (app.initDataUnsafe?.user?.language_code) {
      return app.initDataUnsafe.user.language_code;
    }
    if (app.initData) {
      const params = new URLSearchParams(app.initData);
      const userRaw = params.get("user");
      if (userRaw) {
        const user = JSON.parse(userRaw);
        if (user && typeof user.language_code === "string") {
          return user.language_code;
        }
      }
    }
  } catch {
    // Safe degradation in non-Telegram or unsupported environments
  }
  return null;
}
